import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import type { UserProfile } from '../types/admin';

interface AuthContextType {
  token: string | null;
  email: string | null;
  profile: UserProfile | null;
  login: (token: string, email: string) => void;
  logout: () => void;
  fetchProfile: () => Promise<void>;
  updateProfile: (data: Partial<Pick<UserProfile, 'firstName' | 'lastName' | 'locale'>>) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => localStorage.getItem('token'));
  const [email, setEmail] = useState<string | null>(() => localStorage.getItem('email'));
  const [profile, setProfile] = useState<UserProfile | null>(null);

  const fetchProfile = useCallback(async () => {
    const t = localStorage.getItem('token');
    if (!t) return;
    try {
      const res = await fetch(`${API_BASE}/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (res.ok) {
        const data = await res.json() as UserProfile;
        setProfile(data);
      }
    } catch {
      // non-critical: ignore failures
    }
  }, []);

  const login = useCallback((newToken: string, newEmail: string) => {
    localStorage.setItem('token', newToken);
    localStorage.setItem('email', newEmail);
    setToken(newToken);
    setEmail(newEmail);
    setProfile(null);
    // load profile in background after login
    setTimeout(() => {
      fetch(`${API_BASE}/me`, {
        headers: { Authorization: `Bearer ${newToken}` },
      })
        .then((r) => r.ok ? r.json() : null)
        .then((data: UserProfile | null) => { if (data) setProfile(data); })
        .catch(() => undefined);
    }, 0);
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('email');
    setToken(null);
    setEmail(null);
    setProfile(null);
  }, []);

  const updateProfile = useCallback(async (data: Partial<Pick<UserProfile, 'firstName' | 'lastName' | 'locale'>>) => {
    const t = localStorage.getItem('token');
    if (!t) throw new Error('Not authenticated');
    const res = await fetch(`${API_BASE}/me`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as { error?: string };
      throw new Error(err.error ?? 'Failed to update profile');
    }
    setProfile((prev) => prev ? { ...prev, ...data } : prev);
  }, []);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    const t = localStorage.getItem('token');
    if (!t) throw new Error('Not authenticated');
    const res = await fetch(`${API_BASE}/me/password`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${t}` },
      body: JSON.stringify({ currentPassword, newPassword }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({})) as { error?: string };
      throw new Error(err.error ?? 'Failed to change password');
    }
  }, []);

  return (
    <AuthContext.Provider value={{ token, email, profile, login, logout, fetchProfile, updateProfile, changePassword }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

export { API_BASE };
